import { AlertTriangle, Crosshair, GitBranchPlus, Network, Route, Waves } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";

const USE_CASES = [
  {
    icon: Crosshair,
    title: "Etkilenen son nokta tespiti",
    body: "Bir vana, ana boru hattı veya trafodaki arıza sonucu hangi abonelerin servis dışı kaldığını şebeke topolojisi üzerinden anlık olarak listeleyin. Saha ekibine gitmeden önce etki alanını bilin.",
  },
  {
    icon: Route,
    title: "Etkilenen hat ve segmentlerin görselleştirilmesi",
    body: "Arıza noktasından akış yönüne göre etkilenen hatları haritada renkli olarak vurgulayın. Hangi bölgeye su/gaz/elektrik gidip gitmediği saniyeler içinde görünür.",
  },
  {
    icon: GitBranchPlus,
    title: "Senaryo karşılaştırma",
    body: "Birden fazla arıza noktasını aynı anda işaretleyip etki birleşimini hesaplayın; alternatif vana kapama stratejilerini karşılaştırın, izolasyon planı çıkarın.",
  },
  {
    icon: Waves,
    title: "Akış yönü ve besleme kaynağı analizi",
    body: "Şebeke segmentinin hangi kaynaktan beslendiğini, akış yönünü ve alternatif beslemeleri görün. Planlı kesintilerde hangi vananın kapatılacağına veriyle karar verin.",
  },
];

export function Simulation() {
  return (
    <section
      id="simulasyon"
      className="relative overflow-hidden border-b border-border/60 bg-card/30"
    >
      <div className="container py-20 md:py-28">
        <SectionHeading
          eyebrow="Şebeke Simülasyonu"
          title={
            <>
              Arıza olmadan önce <span className="gradient-text">etki alanını</span> görün.
            </>
          }
          description="Mevcut altyapınızdaki şebeke topolojisi sadece bir harita değil; canlı bir model. Bir noktada arıza, kesinti veya planlı bakım olduğunda hangi hatların ve hangi son noktaların etkileneceğini RADGIS sizin için hesaplar — saha ekibi yola çıkmadan önce."
        />

        <SimulationVisual />

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {USE_CASES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="flex gap-4 rounded-2xl border border-border/60 bg-background p-6"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold tracking-tight">{title}</div>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-6 text-sm text-muted-foreground md:p-7 md:text-base">
          <span className="font-medium text-foreground">
            Sadece harita değil, çalışan bir şebeke modeli.
          </span>{" "}
          Mevcut Altyapı'da tanımladığınız varlıkların birbirine bağlanma kuralları, akış yönleri ve
          hiyerarşisi simülasyonun temelini oluşturur. Veri modelinizi ne kadar zenginleştirirseniz
          simülasyon o kadar güçlü olur — ekstra modül kurmaya, başka bir analiz aracına aktarım
          yapmaya gerek yok.
        </div>
      </div>
    </section>
  );
}

function SimulationVisual() {
  // Network: source -> trunk -> branches -> endpoints
  // Failure point on trunk; downstream branches/endpoints rendered red
  const failureX = 200;
  const failureY = 130;

  const trunk = "M 40 200 L 140 170 L 200 130 L 280 100 L 360 80";
  // Healthy branch (left of failure)
  const healthyBranches = ["M 140 170 L 100 230 L 70 270", "M 140 170 L 170 240 L 200 280"];
  // Affected branches (right of failure)
  const affectedBranches = [
    "M 280 100 L 320 160 L 360 200",
    "M 280 100 L 250 170 L 230 220",
    "M 360 80 L 420 110 L 470 130",
    "M 360 80 L 400 30 L 450 20",
  ];

  const healthyEndpoints: Array<[number, number]> = [
    [70, 270],
    [200, 280],
  ];
  const affectedEndpoints: Array<[number, number]> = [
    [360, 200],
    [230, 220],
    [470, 130],
    [450, 20],
  ];

  return (
    <div className="mt-14 overflow-hidden rounded-2xl border border-border/70 bg-background shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 bg-muted/40 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Network className="h-4 w-4" />
          Arıza Etki Simülasyonu — Akarsu · Bölge 03
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <Legend dotClass="bg-emerald-500" label="Servis veriyor" />
          <Legend dotClass="bg-rose-500" label="Etkilenen" />
          <Legend dotClass="bg-amber-500 ring-2 ring-amber-500/30" label="Arıza noktası" />
        </div>
      </div>

      <div className="grid gap-0 lg:grid-cols-[1.4fr_1fr]">
        <div className="relative aspect-[16/9] grid-bg lg:aspect-auto">
          <svg viewBox="0 0 520 320" className="h-full w-full">
            {/* trunk - half affected */}
            <path
              d={trunk}
              stroke="hsl(var(--muted-foreground))"
              strokeOpacity="0.25"
              strokeWidth="6"
              fill="none"
            />
            {/* healthy branches */}
            {healthyBranches.map((d) => (
              <path
                key={d}
                d={d}
                stroke="rgb(16 185 129)"
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
              />
            ))}
            {/* affected branches */}
            {affectedBranches.map((d) => (
              <path
                key={d}
                d={d}
                stroke="rgb(244 63 94)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="3 4"
                fill="none"
              />
            ))}

            {/* source */}
            <g>
              <circle
                cx={40}
                cy={200}
                r={9}
                fill="rgb(16 185 129)"
                stroke="hsl(var(--background))"
                strokeWidth={2}
              />
              <text x={40} y={225} textAnchor="middle" className="fill-foreground/70" fontSize="10">
                kaynak
              </text>
            </g>

            {/* healthy endpoints */}
            {healthyEndpoints.map(([x, y]) => (
              <circle
                key={`h-${x}-${y}`}
                cx={x}
                cy={y}
                r={5}
                fill="rgb(16 185 129)"
                stroke="hsl(var(--background))"
                strokeWidth={2}
              />
            ))}

            {/* affected endpoints */}
            {affectedEndpoints.map(([x, y]) => (
              <circle
                key={`a-${x}-${y}`}
                cx={x}
                cy={y}
                r={5}
                fill="rgb(244 63 94)"
                stroke="hsl(var(--background))"
                strokeWidth={2}
              />
            ))}

            {/* failure point */}
            <g>
              <circle
                cx={failureX}
                cy={failureY}
                r={14}
                fill="rgb(245 158 11)"
                fillOpacity={0.18}
              />
              <circle
                cx={failureX}
                cy={failureY}
                r={8}
                fill="rgb(245 158 11)"
                stroke="hsl(var(--background))"
                strokeWidth={2}
              />
            </g>
          </svg>

          <div className="absolute left-3 top-3 rounded-md border border-border/60 bg-background/85 px-2 py-1 text-[11px] backdrop-blur">
            Vana V-1042 kapatıldı
          </div>
        </div>

        <div className="border-t border-border/60 p-6 md:p-7 lg:border-l lg:border-t-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Etki Özeti
          </div>
          <div className="mt-3 space-y-3">
            <Stat
              tone="rose"
              icon={AlertTriangle}
              label="Servis dışı kalan abone"
              value="412"
              hint="4 mahalle · Bölge 03"
            />
            <Stat
              tone="rose"
              icon={Route}
              label="Etkilenen hat segmenti"
              value="6"
              hint="≈ 1.8 km"
            />
            <Stat
              tone="emerald"
              icon={Crosshair}
              label="Servis veren bölgeler"
              value="2"
              hint="alternatif besleme aktif"
            />
          </div>

          <div className="mt-5 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
            Saha ekibi yola çıkmadan önce hangi abonelere bilgi verileceği, hangi alternatif vananın
            açılacağı haritada görünür.
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend({ dotClass, label }: { dotClass: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span className={`h-2 w-2 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

function Stat({
  tone,
  icon: Icon,
  label,
  value,
  hint,
}: {
  tone: "rose" | "emerald";
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint: string;
}) {
  const colors =
    tone === "rose"
      ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
      : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-background p-3">
      <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${colors}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="flex items-baseline gap-2">
          <div className="text-lg font-semibold leading-tight">{value}</div>
          <div className="truncate text-[11px] text-muted-foreground">{hint}</div>
        </div>
      </div>
    </div>
  );
}
